import React from 'react'
import { Box, Skeleton, Stack, type SxProps, type Theme, Tooltip, Typography } from '@mui/material'

import { borderRadius, borders, lightTokens, spacing, typography } from '~tokens/internal'

import Icon from './Icon'

export type KPITrend = 'up' | 'down' | 'neutral'

export interface KPIProps {
  title: string
  value?: string | number
  trend?: KPITrend
  trendValue?: string
  timePeriod?: string
  /** Set false when a rising trend is bad (e.g. cost), so up renders red and down green. */
  upIsGood?: boolean
  info?: string
  icon?: React.ReactNode
  showTrendIcon?: boolean
  loading?: boolean
  children?: React.ReactNode
  sx?: SxProps<Theme>
}

const getTrendColor = (trend: KPITrend, upIsGood: boolean): string => {
  if (trend === 'neutral') return lightTokens.text.secondary
  return (trend === 'up') === upIsGood ? lightTokens.success.main : lightTokens.error.main
}

const TREND_ICON_SIZE = 16

const trendIconMap: Record<KPITrend, React.ReactNode> = {
  up: <Icon name='arrowUp' size={TREND_ICON_SIZE} />,
  down: <Icon name='arrowDown' size={TREND_ICON_SIZE} />,
  neutral: <Icon name='remove' size={TREND_ICON_SIZE} />,
}

const KPI: React.FC<KPIProps> = ({
  title,
  value,
  trend,
  trendValue,
  timePeriod,
  upIsGood = true,
  info,
  icon,
  showTrendIcon = false,
  loading = false,
  children,
  sx,
}) => {
  return (
    <Box
      sx={{
        backgroundColor: lightTokens.background.default,
        border: `${borders.width.thin} solid ${lightTokens.border}`,
        borderRadius: borderRadius.sm,
        p: spacing.xs,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography
          variant='body1'
          sx={{
            fontWeight: typography.fontWeight.medium,
            color: lightTokens.text.secondary,
            letterSpacing: typography.letterSpacing.wide,
          }}
        >
          {title}
        </Typography>
        {info && (
          <Tooltip title={info} placement='bottom-start'>
            <Icon name='info' color={lightTokens.text.secondary} fontSize='medium' />
          </Tooltip>
        )}
      </Box>

      {loading ? (
        <Skeleton variant='text' width='60%' sx={{ fontSize: typography.fontSize.xl }} />
      ) : children ? (
        children
      ) : (
        <>
          <Stack
            direction='row'
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            {icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: lightTokens.text.secondary,
                }}
              >
                {icon}
              </Box>
            )}
            <Typography
              sx={{
                fontFamily: typography.fontFamily.heading,
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.regular,
                color: lightTokens.text.primary,
              }}
            >
              {value}
            </Typography>
          </Stack>

          {(trendValue || timePeriod) && (
            <Stack
              direction='row'
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              {trend && showTrendIcon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: getTrendColor(trend, upIsGood),
                  }}
                >
                  {trendIconMap[trend]}
                </Box>
              )}
              {trendValue && (
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: typography.fontWeight.medium,
                    color: trend ? getTrendColor(trend, upIsGood) : lightTokens.text.secondary,
                  }}
                >
                  {trendValue}
                </Typography>
              )}
              {timePeriod && (
                <Typography
                  variant='subtitle2'
                  sx={{
                    fontWeight: typography.fontWeight.medium,
                    color: lightTokens.text.secondary,
                  }}
                >
                  {timePeriod}
                </Typography>
              )}
            </Stack>
          )}
        </>
      )}
    </Box>
  )
}

export default KPI
